import { CircularProgress } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BUNDLES_PER_PAGE } from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import { useAuthStore } from 'services/hooks';
import { AudioProvider } from 'services/contexts/AudioProvider';
import BundleContainer from '../BundleContainer';
import StepGuide from '../StepsGuide';
import {
  PreloaderContainer,
  ResultContainer,
  StyledBundleResults,
  StyledButton,
} from './styled';
import useBundles from './useBundles';

const Bundles = () => {
  const { t } = useTranslation();

  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;

  const { load, loadMore, areAllLoaded, isLoading, isGenerating, bundles } =
    useBundles();

  const ref = useRef<HTMLDivElement>(null);
  const scrollToBottom = useCallback(() => {
    const container = ref.current;
    if (!container) {
      return;
    }
    requestAnimationFrame(() =>
      container.scrollIntoView({ behavior: 'smooth' })
    );
  }, [ref]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    (async () => {
      await load();
      scrollToBottom();
    })();
  }, [load, scrollToBottom, isLoggedIn]);

  useEffect(() => {
    if (isGenerating) {
      scrollToBottom();
    }
  }, [isGenerating, bundles.length, scrollToBottom]);

  const showStepGuide = !isLoggedIn || !(bundles.length || isLoading);

  return (
    <ResultContainer>
      {isLoading && (
        <PreloaderContainer>
          <CircularProgress size={32} />
        </PreloaderContainer>
      )}
      {showStepGuide ? (
        <StepGuide />
      ) : (
        <StyledBundleResults data-testid="ai-result-container" ref={ref}>
          <AudioProvider>
            {bundles.map((bundle) => (
              <BundleContainer key={bundle.id} bundle={bundle} />
            ))}
          </AudioProvider>
          {bundles.length >= BUNDLES_PER_PAGE && (
            <StyledButton
              onClick={loadMore}
              loading={isLoading}
              disabled={areAllLoaded}
            >
              {t('general:action:loadMore')}
            </StyledButton>
          )}
        </StyledBundleResults>
      )}
    </ResultContainer>
  );
};

export default Bundles;
